// PhotographyServices.js - Photography-themed Services section component
import { DEVICES } from '../../../constants/breakpoints';

/**
 * Creates a photography-themed Services section with service cards
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the services section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The services elements
 */
export const createPhotographyServicesElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const servicesSectionClass = 'photography-services-section';
  const servicesContainerClass = 'photography-services-container';
  const servicesHeaderClass = 'photography-services-header';
  const servicesTitleClass = 'photography-services-title';
  const servicesSubtitleClass = 'photography-services-subtitle';
  const servicesGridClass = 'photography-services-grid';
  const serviceCardClass = 'photography-service-card';
  const serviceIconClass = 'photography-service-icon';
  const serviceNameClass = 'photography-service-name';
  const serviceDescClass = 'photography-service-description';
  const servicePriceClass = 'photography-service-price';
  const serviceFeatureListClass = 'photography-service-features';
  const serviceFeatureClass = 'photography-service-feature';
  const serviceButtonClass = 'photography-service-button';
  const servicesPatternOverlayClass = 'photography-services-pattern-overlay';

  // Desktop Styles (default) - Photography theme with elegant dark design
  const servicesSectionStyles = {
    position: 'relative',
    paddingTop: '100px',
    paddingBottom: '100px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#0f0f0f',
    background: `
      radial-gradient(circle at 30% 20%, rgba(212, 175, 55, 0.08) 0%, transparent 50%),
      radial-gradient(circle at 70% 80%, rgba(212, 175, 55, 0.06) 0%, transparent 50%),
      linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 50%, #0f0f0f 100%)
    `,
    color: '#f5f5f5',
    width: '100%',
    overflow: 'hidden'
  };

  const servicesPatternOverlayStyles = {
    position: 'absolute',
    top: '0',
    left: '0',
    width: '100%',
    height: '100%',
    backgroundImage: `
      radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.02) 1px, transparent 1px),
      radial-gradient(circle at 80% 70%, rgba(212, 175, 55, 0.02) 1px, transparent 1px)
    `,
    backgroundSize: '80px 80px, 120px 120px',
    backgroundPosition: '0 0, 60px 60px',
    zIndex: '0',
    opacity: '0.5'
  };

  const servicesContainerStyles = {
    maxWidth: '1400px',
    margin: '0 auto',
    position: 'relative',
    zIndex: '1'
  };

  const servicesHeaderStyles = {
    textAlign: 'center',
    marginBottom: '80px'
  };

  const servicesTitleStyles = {
    fontSize: '48px',
    fontWeight: '300',
    color: '#f5f5f5',
    marginBottom: '20px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '2px',
    lineHeight: '1.2',
    position: 'relative'
  };

  const servicesSubtitleStyles = {
    fontSize: '18px',
    color: 'rgba(245, 245, 245, 0.8)',
    maxWidth: '600px',
    margin: '0 auto',
    lineHeight: '1.6',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '300'
  };

  const servicesGridStyles = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
    gap: '40px',
    marginTop: '60px'
  };

  const serviceCardStyles = {
    background: `
      linear-gradient(135deg, rgba(212, 175, 55, 0.08) 0%, rgba(212, 175, 55, 0.04) 100%),
      rgba(26, 26, 26, 0.6)
    `,
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(212, 175, 55, 0.2)',
    borderRadius: '0',
    padding: '40px 30px',
    textAlign: 'center',
    transition: 'all 0.4s ease',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
    '&:hover': {
      transform: 'translateY(-8px)',
      border: '1px solid rgba(212, 175, 55, 0.4)',
      background: `
        linear-gradient(135deg, rgba(212, 175, 55, 0.12) 0%, rgba(212, 175, 55, 0.08) 100%),
        rgba(26, 26, 26, 0.8)
      `,
      boxShadow: `
        0 20px 40px rgba(0, 0, 0, 0.4),
        0 0 20px rgba(212, 175, 55, 0.2)
      `
    }
  };

  const serviceIconStyles = {
    fontSize: '48px',
    color: '#d4af37',
    marginBottom: '25px',
    display: 'block',
    transition: 'all 0.3s ease'
  };

  const serviceNameStyles = {
    fontSize: '24px',
    fontWeight: '400',
    color: '#f5f5f5',
    marginBottom: '15px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '1px'
  };

  const serviceDescStyles = {
    fontSize: '15px',
    color: 'rgba(245, 245, 245, 0.8)',
    lineHeight: '1.6',
    marginBottom: '25px',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '300'
  };

  const servicePriceStyles = {
    fontSize: '32px',
    fontWeight: '300',
    color: '#d4af37',
    marginBottom: '25px',
    fontFamily: '"Playfair Display", serif',
    letterSpacing: '1px'
  };

  const serviceFeatureListStyles = {
    listStyle: 'none',
    padding: '0',
    margin: '0 0 30px 0'
  };

  const serviceFeatureStyles = {
    fontSize: '14px',
    color: 'rgba(245, 245, 245, 0.9)',
    padding: '8px 0',
    borderBottom: '1px solid rgba(212, 175, 55, 0.1)',
    fontFamily: '"Inter", sans-serif',
    fontWeight: '300',
    position: 'relative',
    paddingLeft: '20px'
  };

  const serviceButtonStyles = {
    padding: '14px 30px',
    background: `
      linear-gradient(135deg, rgba(212, 175, 55, 0.1) 0%, rgba(212, 175, 55, 0.05) 100%),
      rgba(26, 26, 26, 0.3)
    `,
    color: '#d4af37',
    border: '1px solid rgba(212, 175, 55, 0.4)',
    borderRadius: '0',
    fontSize: '14px',
    fontWeight: '400',
    cursor: 'pointer',
    transition: 'all 0.4s ease',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: '"Inter", sans-serif',
    backdropFilter: 'blur(10px)',
    width: '100%'
  };

  // Tablet Styles
  const servicesTabletStyles = {
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const servicesTitleTabletStyles = {
    fontSize: '40px'
  };

  const servicesSubtitleTabletStyles = {
    fontSize: '16px'
  };

  const servicesGridTabletStyles = {
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '30px'
  };

  const serviceCardTabletStyles = {
    padding: '35px 25px'
  };

  // Mobile Styles
  const servicesMobileStyles = {
    paddingTop: '60px',
    paddingBottom: '60px',
    paddingLeft: '15px',
    paddingRight: '15px'
  };

  const servicesTitleMobileStyles = {
    fontSize: '32px'
  };

  const servicesSubtitleMobileStyles = {
    fontSize: '15px'
  };

  const servicesGridMobileStyles = {
    gridTemplateColumns: '1fr',
    gap: '25px'
  };

  const serviceCardMobileStyles = {
    padding: '30px 20px'
  };

  const serviceIconMobileStyles = {
    fontSize: '40px'
  };

  const serviceNameMobileStyles = {
    fontSize: '20px'
  };

  const servicePriceMobileStyles = {
    fontSize: '28px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(servicesSectionClass, servicesSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(servicesPatternOverlayClass, servicesPatternOverlayStyles);
    newRegistry[DEVICES.DESKTOP].set(servicesContainerClass, servicesContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(servicesHeaderClass, servicesHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(servicesTitleClass, servicesTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(servicesSubtitleClass, servicesSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(servicesGridClass, servicesGridStyles);
    newRegistry[DEVICES.DESKTOP].set(serviceCardClass, serviceCardStyles);
    newRegistry[DEVICES.DESKTOP].set(serviceIconClass, serviceIconStyles);
    newRegistry[DEVICES.DESKTOP].set(serviceNameClass, serviceNameStyles);
    newRegistry[DEVICES.DESKTOP].set(serviceDescClass, serviceDescStyles);
    newRegistry[DEVICES.DESKTOP].set(servicePriceClass, servicePriceStyles);
    newRegistry[DEVICES.DESKTOP].set(serviceFeatureListClass, serviceFeatureListStyles);
    newRegistry[DEVICES.DESKTOP].set(serviceFeatureClass, serviceFeatureStyles);
    newRegistry[DEVICES.DESKTOP].set(serviceButtonClass, serviceButtonStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(servicesSectionClass, servicesTabletStyles);
    newRegistry[DEVICES.TABLET].set(servicesTitleClass, servicesTitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(servicesSubtitleClass, servicesSubtitleTabletStyles);
    newRegistry[DEVICES.TABLET].set(servicesGridClass, servicesGridTabletStyles);
    newRegistry[DEVICES.TABLET].set(serviceCardClass, serviceCardTabletStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(servicesSectionClass, servicesMobileStyles);
    newRegistry[DEVICES.MOBILE].set(servicesTitleClass, servicesTitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(servicesSubtitleClass, servicesSubtitleMobileStyles);
    newRegistry[DEVICES.MOBILE].set(servicesGridClass, servicesGridMobileStyles);
    newRegistry[DEVICES.MOBILE].set(serviceCardClass, serviceCardMobileStyles);
    newRegistry[DEVICES.MOBILE].set(serviceIconClass, serviceIconMobileStyles);
    newRegistry[DEVICES.MOBILE].set(serviceNameClass, serviceNameMobileStyles);
    newRegistry[DEVICES.MOBILE].set(servicePriceClass, servicePriceMobileStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    servicesSectionClass,
    servicesPatternOverlayClass,
    servicesContainerClass,
    servicesHeaderClass,
    servicesTitleClass,
    servicesSubtitleClass,
    servicesGridClass,
    serviceCardClass,
    serviceIconClass,
    serviceNameClass,
    serviceDescClass,
    servicePriceClass,
    serviceFeatureListClass,
    serviceFeatureClass,
    serviceButtonClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const patternOverlayId = Date.now() + 1;
  const containerId = Date.now() + 2;
  const headerId = Date.now() + 3;
  const titleId = Date.now() + 4;
  const subtitleId = Date.now() + 5;
  const gridId = Date.now() + 6;

  // Service cards data
  const services = [
    {
      icon: '📸',
      name: 'Portrait Photography',
      description: 'Professional portrait sessions capturing your unique personality and style with artistic flair.',
      price: 'From $299',
      features: [
        '2-hour photo session',
        '50+ edited high-resolution images',
        'Online gallery access',
        'Print release included',
        'Wardrobe consultation'
      ]
    },
    {
      icon: '💒',
      name: 'Wedding Photography',
      description: 'Complete wedding day coverage with cinematic storytelling and timeless elegance.',
      price: 'From $1,999',
      features: [
        '8-hour wedding coverage',
        '500+ edited images',
        'Engagement session included',
        'Online gallery & USB drive',
        'Second photographer option'
      ]
    },
    {
      icon: '🏢',
      name: 'Commercial Photography',
      description: 'Professional business and product photography to elevate your brand presence.',
      price: 'From $599',
      features: [
        'Brand consultation',
        'Studio or location shoot',
        'Professional retouching',
        'Commercial usage rights',
        'Rush delivery available'
      ]
    }
  ];

  // Create service card elements
  let currentId = gridId + 1;
  const serviceCardElements = [];

  services.forEach((service, index) => {
    const cardId = currentId++;
    const iconId = currentId++;
    const nameId = currentId++;
    const descId = currentId++;
    const priceId = currentId++;
    const featureListId = currentId++;
    const buttonId = currentId++;

    // Create feature elements
    const featureElements = [];
    const featureIds = [];
    service.features.forEach((feature, featureIndex) => {
      const featureId = currentId++;
      featureIds.push(featureId);
      featureElements.push({
        id: featureId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'li',
        name: `Service Feature ${featureIndex + 1}`,
        parentId: featureListId,
        hierarchyName: getHierarchyName('li'),
        className: serviceFeatureClass,
        styles: {},
        children: [],
        text: feature,
        attributes: {
          'data-element': 'service-feature'
        }
      });
    });

    // Add card elements
    serviceCardElements.push(
      // Service Card
      {
        id: cardId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Service Card ${index + 1}`,
        parentId: gridId,
        hierarchyName: getHierarchyName('div'),
        className: serviceCardClass,
        styles: {},
        children: [iconId, nameId, descId, priceId, featureListId, buttonId],
        text: '',
        attributes: {
          'data-element': 'service-card',
          'data-service': service.name.toLowerCase().replace(/\s+/g, '-')
        }
      },
      // Service Icon
      {
        id: iconId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'span',
        name: `Service Icon ${index + 1}`,
        parentId: cardId,
        hierarchyName: getHierarchyName('span'),
        className: serviceIconClass,
        styles: {},
        children: [],
        text: service.icon,
        attributes: {
          'data-element': 'service-icon'
        }
      },
      // Service Name
      {
        id: nameId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'h3',
        name: `Service Name ${index + 1}`,
        parentId: cardId,
        hierarchyName: getHierarchyName('h3'),
        className: serviceNameClass,
        styles: {},
        children: [],
        text: service.name,
        attributes: {
          'data-element': 'service-name'
        }
      },
      // Service Description
      {
        id: descId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'p',
        name: `Service Description ${index + 1}`,
        parentId: cardId,
        hierarchyName: getHierarchyName('p'),
        className: serviceDescClass,
        styles: {},
        children: [],
        text: service.description,
        attributes: {
          'data-element': 'service-description'
        }
      },
      // Service Price
      {
        id: priceId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        name: `Service Price ${index + 1}`,
        parentId: cardId,
        hierarchyName: getHierarchyName('div'),
        className: servicePriceClass,
        styles: {},
        children: [],
        text: service.price,
        attributes: {
          'data-element': 'service-price'
        }
      },
      // Service Features List
      {
        id: featureListId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'ul',
        name: `Service Features ${index + 1}`,
        parentId: cardId,
        hierarchyName: getHierarchyName('ul'),
        className: serviceFeatureListClass,
        styles: {},
        children: featureIds,
        text: '',
        attributes: {
          'data-element': 'service-features'
        }
      },
      // Service Button
      {
        id: buttonId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'button',
        name: `Service Button ${index + 1}`,
        parentId: cardId,
        hierarchyName: getHierarchyName('button'),
        className: serviceButtonClass,
        styles: {},
        children: [],
        text: 'Book Now',
        attributes: {
          'data-element': 'service-button'
        },
        linkTo: 'book-session'
      }
    );

    // Add feature elements
    serviceCardElements.push(...featureElements);
  });

  // Create all elements
  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Photography Services Section',
      parentId: null,
      className: servicesSectionClass,
      styles: {},
      children: [patternOverlayId, containerId],
      text: '',
      attributes: {
        'data-element': 'services-section',
        'data-component': 'section',
        'data-section-type': 'services',
        'data-page-id': currentPageId
      }
    },
    // Pattern Overlay
    {
      id: patternOverlayId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Services Pattern Overlay',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: servicesPatternOverlayClass,
      styles: {},
      children: [],
      text: '',
      attributes: {
        'data-element': 'services-pattern-overlay'
      }
    },
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Services Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: servicesContainerClass,
      styles: {},
      children: [headerId, gridId],
      text: '',
      attributes: {
        'data-element': 'services-container'
      }
    },
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Services Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: servicesHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'services-header'
      }
    },
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Services Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: servicesTitleClass,
      styles: {},
      children: [],
      text: 'Our Photography Services',
      attributes: {
        'data-element': 'services-title'
      }
    },
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Services Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: servicesSubtitleClass,
      styles: {},
      children: [],
      text: 'Professional photography services tailored to capture your most precious moments with artistic excellence and timeless elegance.',
      attributes: {
        'data-element': 'services-subtitle'
      }
    },
    // Services Grid
    {
      id: gridId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Services Grid',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: servicesGridClass,
      styles: {},
      children: serviceCardElements.filter(el => el.parentId === gridId).map(el => el.id),
      text: '',
      attributes: {
        'data-element': 'services-grid'
      }
    },
    ...serviceCardElements
  ];

  return {
    servicesSection: newElements[0],
    elements: newElements
  };
}; 